/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Junie (AI), 03-06-2026
 *
 * Registreert cytoscape collection-extensies die in de hele Dynalearn-app worden gebruikt.
 * Deze module wordt eenmalig (top-level side-effect) geladen via een import in
 * `dl-cytocanvas-base.directive.ts` en draait dus vóór de `cytoscape(opties)`-call.
 *
 * Komt rechtstreeks uit de oude AngularJS `app/service/cytoscape.service.js`.
 * De core-functies (`emptyArea`, `renderedToModelCoords`, `fitToMaxZoom`) zijn niet hier
 * geregistreerd: die zitten als methoden op `DlCytoCanvasBaseDirective`.
 */
import cytoscape from 'cytoscape';
import { elements } from 'dynalearn-common/lib/elements';

/**
 * Vlag om dubbele registratie te voorkomen (Angular kan modules vaker dan eens importeren in dev/HMR).
 */
let geregistreerd = false;

if (!geregistreerd) {
    geregistreerd = true;

    /**
     * Map een collection op een array met id's
     */
    cytoscape('collection', 'toIdArray', function (this: cytoscape.CollectionReturnValue) {
        return this.map((n) => n.id());
    });

    /**
     * Element-definitie ophalen. Gebruik deze i.p.v. `scratch('definition')`,
     * de definitie wordt op de scratch gecached bij eerste lookup.
     */
    cytoscape('collection', 'definition', function (this: cytoscape.CollectionReturnValue) {
        let sdef = this.scratch('definition');
        if (!sdef) {
            this.scratch('definition', sdef = elements.getDefinition(this.data('type')));
        }
        return sdef;
    });

    /**
     * Dynalearn-versie van `outgoers`: houdt rekening met `edgeHandle`-nodes.
     */
    cytoscape('collection', 'outElements', function (this: cytoscape.CollectionReturnValue, selector?: string) {
        const thiscy = this.cy();
        let outg = thiscy.collection();
        this.forEach((element) => {
            const uitgangen = element.add('[type="edgeHandle"][handleParent="' + element.id() + '"]');
            outg = outg.add(uitgangen.outgoers('[type!="edgeHandle"]'));
            uitgangen.outgoers('[type="edgeHandle"]').forEach((otherHandle) => {
                outg = outg.add(thiscy.getElementById(otherHandle.data('handleParent')));
            });
        });
        return selector ? outg.filter(selector) : outg;
    });

    /**
     * Dynalearn-versie van `incomers`: houdt rekening met `edgeHandle`-nodes.
     */
    cytoscape('collection', 'inElements', function (this: cytoscape.CollectionReturnValue, selector?: string) {
        const thiscy = this.cy();
        let inc = thiscy.collection();
        this.forEach((element) => {
            const uitgangen = element.add('[type="edgeHandle"][handleParent="' + element.id() + '"]');
            inc = inc.add(uitgangen.incomers('[type!="edgeHandle"]'));
            uitgangen.incomers('[type="edgeHandle"]').forEach((otherHandle) => {
                inc = inc.add(thiscy.getElementById(otherHandle.data('handleParent')));
            });
        });
        return selector ? inc.filter(selector) : inc;
    });

    /**
     * Geef de slope (m) en intercept (b) van een edge terug, zodat de lijn waarop het
     * lijnstuk tussen source en target ligt wordt gedefinieerd als y = mx + b.
     * Bij een verticale lijn wordt niet `{m,b}` maar `{x}` teruggegeven; check daar dus op.
     */
    cytoscape('collection', 'lijnfunctie', function (this: cytoscape.CollectionReturnValue) {
        const edge = this.first();
        if (!edge.is('edge')) {
            throw new Error('lijnfunctie() is alleen voor edges');
        }
        const A = (edge as cytoscape.EdgeSingular).source().position();
        const B = (edge as cytoscape.EdgeSingular).target().position();
        const dy = B.y - A.y;
        const dx = B.x - A.x;
        if (dx !== 0) {
            const m = dy / dx;
            const b = A.y - m * A.x;
            return { m, b };
        }
        return { x: A.x };
    });
}
